/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { css } from '@emotion/react'
import cx from 'classnames'
import { isArray } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { matchPath, NavLink, useLocation, useParams } from 'react-router-dom'

import { EuiBetaBadge, EuiFlexGroup, EuiFlexItem, EuiSideNav } from '@elastic/eui'

import { useConnectedClustersMenu } from '@modules/ccm-admin-lib/navigation'
import type { Organization, SaasUserResponse } from '@modules/cloud-api/v1/types'
import { FormattedMessage } from '@modules/i18n'
import { buildProjectsMenu } from '@modules/project-admin-pages/navigation'
import { startingWith } from '@modules/routing/startingWith'
import { organizationOverviewUrl, organizationsUrl } from '@modules/urls/organizations'
import { userCreateUrl, userOverviewUrl, usersUrl } from '@modules/urls/users'
import Feature from '@modules/utils/feature'
import type { EuiSideNavItem } from '@modules/utils/navigation'
import { createItem } from '@modules/utils/navigation'

import { getDeploymentHealthProblems, getHealthStatusNavigationIcon } from '@/lib/healthProblems'
import { isPortalUrl } from '@/lib/portal'
import { getSliderPrettyName } from '@/lib/sliders/messages'
import { getSupportedSliderInstanceTypesWithoutEs } from '@/lib/sliders/support'
import { isSliderSupportedForDeployment } from '@/lib/stackDeployments/selectors/deploymentTemplates'
import { getDisplayName } from '@/lib/stackDeployments/selectors/display'
import { getVersion, hasSizedSliderResource } from '@/lib/stackDeployments/selectors/fundamentals'
import {
  activityFeedUrl,
  adminconsolesUrl,
  apiKeysUrl,
  authenticationProvidersUrl,
  clusterSnapshotUrl,
  containerSetsUrl,
  containerSetUrl,
  containerUrl,
  createDeploymentUrl,
  createSnapshotRepositoryUrl,
  deploymentAdvancedEditUrl,
  deploymentsUrl,
  deploymentUrl,
  deploymentUrls,
  editSnapshotRepositoryUrl,
  elasticStackVersionsUrl,
  heapDumpsUrl,
  hostsUrl,
  hostUrl,
  logsMonitoringUrl,
  manageAllUsersApiKeysUrl,
  managementUrl,
  manageNativeUsersUrl,
  manageRegionUrl,
  platformTrustManagementUrl,
  platformUrl,
  regionSecurityUrl,
  regionSettingsUrl,
  regionsUrl,
  regionUrl,
  snapshotRepositoriesUrl,
  topologyUrl,
  topologyViewDeploymentTemplateUrl,
} from '@/lib/urlBuilder'
import { isFeatureActivated } from '@/store'

import messages from './navigationMessages'

import type { ReactElement, ReactNode } from 'react'
import type { AllProps } from './types'

type RenderNavLinkProps = {
  href?: string
  children: ReactNode
  className?: string
  exact?: boolean
  beta?: boolean
  'data-test-id'?: string
}

const ChromeNavigation = (props: AllProps) => {
  const [isSideNavOpenOnMobile, setIsSideNavOpenOnMobile] = useState(false)
  const { pathname } = useLocation()
  const params = useParams()
  const { formatMessage } = useIntl()

  const {
    regionId,
    saasUserProfile,
    organization,
    lookupSaasUsers,
    manageRbacFeature,
    serverlessFeature,
    showUserApiKeys,
    permissions: {
      hasSearchDeploymentsPermission,
      hasSearchAllocatorsPermission,
      hasListApiKeysPermission,
      hasListUsersPermission,
    },
    deployment,
    stackDeployment,
    fetchDeploymentTemplates,
  } = props

  const stackVersion = stackDeployment
    ? getVersion({ deployment: stackDeployment })
    : deployment?.plan.version

  useEffect(() => {
    if (!isDeploymentActive(pathname)) {
      return
    }

    if (!regionId || !stackVersion) {
      return
    }

    fetchDeploymentTemplates({ regionId, stackVersion, showMaxZones: true })
  }, [regionId, stackVersion])

  useEffect(() => {
    onNavigate(pathname)
  }, [pathname])

  const { userId, organizationId } = params

  const deploymentActive = isDeploymentActive(pathname)
  const platformActive = isPlatformActive(pathname)
  const managementActive = isManagementActive(pathname)
  const createDeploymentActive = isCreateDeploymentActive(pathname)
  const euiSideNavItems: EuiSideNavItem[] = []

  if (hasSearchDeploymentsPermission) {
    euiSideNavItems.push(buildDeploymentMenu(createDeploymentActive, deploymentActive))
  }

  if (serverlessFeature) {
    euiSideNavItems.push(
      buildProjectsMenu({
        formatMessage,
        location,
      }),
    )
  }

  const connectedClustersMenu = useConnectedClustersMenu()

  euiSideNavItems.push(...connectedClustersMenu)

  if (hasSearchAllocatorsPermission) {
    euiSideNavItems.push(buildPlatformMenu(platformActive))
  }

  if (manageRbacFeature && hasListUsersPermission) {
    euiSideNavItems.push(buildUsersMenu(managementActive))
  }

  if (hasSearchDeploymentsPermission) {
    euiSideNavItems.push(
      createItem({
        name: formatMessage(messages.activityFeed),
        href: activityFeedUrl(),
        'data-test-id': `activity-feed-section-link`,
      }),
    )
  }

  const organizationItems = organizationNavigation({
    organizationId: organizationId!,
    organization,
  })

  if (lookupSaasUsers) {
    euiSideNavItems.push(
      createItem({
        name: formatMessage(messages.organizations),
        href: organizationsUrl(),
        items: organizationItems,
      }),
    )
  }

  const userItems = userNavigation({ userId: userId!, profile: saasUserProfile })

  if (lookupSaasUsers) {
    euiSideNavItems.push(
      createItem({
        name: formatMessage(messages.saasUsers),
        href: usersUrl(),
        items: userItems,
      }),
    )
  }

  if (showUserApiKeys && hasListApiKeysPermission) {
    euiSideNavItems.push(
      createItem({
        name: formatMessage(messages.userApiKeys),
        href: apiKeysUrl(),
        'data-test-id': `top-level-api-keys-link`,
      }),
    )
  }

  const euiSideNavItemsWithSelectedItem = setSelectedItem(euiSideNavItems, itemIsSelected)

  return (
    <EuiSideNav
      mobileTitle={formatMessage(messages.mobileTitle)}
      isOpenOnMobile={isSideNavOpenOnMobile}
      toggleOpenOnMobile={toggleOpenOnMobile}
      items={euiSideNavItemsWithSelectedItem}
      renderItem={renderNavLink}
      className='cloud-navigation'
      css={css`
        .euiSideNavItem {
          margin: 0;
          padding: 0;
        }
      `}
    />
  )

  function itemIsSelected(item: EuiSideNavItem): boolean {
    if (!item.href) {
      return false
    }

    if (item.isActive) {
      // use explicit item-specific override if present, e.g. to mark as active a parent item with a non-overlapping path
      return item.isActive()
    }

    const result = Boolean(matchPath(startingWith(item.href), pathname))

    return result
  }

  function buildDeploymentMenu(createDeploymentActive, deploymentActive) {
    const createDeploymentItem = createDeploymentActive
      ? [
          createItem({
            name: formatMessage(messages.createDeployment),
            href: createDeploymentUrl(),
            'data-test-id': `deployment-section-create-deployment-link`,
          }),
        ]
      : undefined

    const deploymentItems = deploymentActive ? deploymentNavigation() : createDeploymentItem

    return createItem({
      name: formatMessage(messages.deployments),
      href: deploymentsUrl(),
      items: deploymentItems,
      'data-test-id': `deployment-section-link`,
    })
  }

  function buildPlatformMenu(platformActive) {
    const { defaultRegionId, regionId } = props

    const platformItems =
      Boolean(platformActive) && regionId != null ? platformNavigation(regionId) : undefined

    return createItem({
      name: formatMessage(messages.platform),
      href: defaultRegionId ? regionUrl(defaultRegionId) : regionsUrl(),
      items: platformItems,

      // necessary because otherwise Platform lights up when Users is active
      isActive: () => platformActive,
      'data-test-id': `platform-link`,
    })
  }

  function buildUsersMenu(usersActive) {
    const { defaultRegionId, regionId } = props

    const manageUsersItems =
      Boolean(usersActive) && regionId != null
        ? manageUsersNavigation(regionId || defaultRegionId)
        : undefined

    return createItem({
      name: formatMessage(messages.users),
      href: defaultRegionId ? manageRegionUrl(defaultRegionId) : managementUrl(),
      'data-test-id': `management-section-link`,
      items: manageUsersItems,
    })
  }

  function toggleOpenOnMobile() {
    setIsSideNavOpenOnMobile((prevState) => !prevState)
  }

  function onNavigate(newPath: string) {
    if (!isPortalUrl(newPath)) {
      setIsSideNavOpenOnMobile(false)
    }
  }

  function deploymentNavigation() {
    const {
      isAdminConsole,
      deploymentTemplate,
      stackDeploymentId,
      stackDeployment,
      eolStatus,
      health,
      deploymentHealthRequest,
      isOneConsoleEnabled,
    } = props

    const { snapshotName } = params

    if (!stackDeployment || !stackDeploymentId) {
      return []
    }

    const urls = deploymentUrls(stackDeploymentId)
    const version = getVersion({ deployment: stackDeployment })

    const advancedDeploymentEditActive = isAdvancedDeploymentEditActive(pathname)

    const editSubItems: EuiSideNavItem[] = []

    if (advancedDeploymentEditActive) {
      editSubItems.push(
        createItem({
          name: formatMessage(messages.advancedEdit),
          href: urls.advancedEdit,
          'data-test-id': `deployment-advanced-edit-link`,
        }),
      )
    }

    // Compute all the deployment problems so we can figure out
    // if we should show a warning icon in the healthStatusItem
    // from the navigation bar.
    const [problems] = getDeploymentHealthProblems({
      deployment: stackDeployment,
      eolStatus,
      health,
      deploymentHealthRequest,
    })

    const monitoringSectionNavItems = createItem({
      name: formatMessage(messages.deploymentMonitoring),
      href: urls.monitoring,
      'data-test-id': `deployment-index-monitoring-link`,
      items: [
        createItem({
          name: formatMessage(messages.healthStatus),
          href: urls.healthStatus,
          icon: getHealthStatusNavigationIcon(problems),
          'data-test-id': `deployment-health-status`,
        }),
        createItem({
          name: formatMessage(messages.loggingMonitoring),
          href: logsMonitoringUrl(stackDeploymentId),
          'data-test-id': `deployment-index-logging-monitoring-link`,
        }),
      ],
    })

    const elasticsearchSubItems = [
      createItem({
        name: formatMessage(messages.clusterSnapshots),
        href: urls.snapshots,
        'data-test-id': `deployment-index-elasticsearch-snapshots-link`,
        items: snapshotName
          ? [
              createItem({
                name: snapshotName,
                href: clusterSnapshotUrl(stackDeploymentId, snapshotName),
              }),
            ]
          : undefined,
      }),
      createItem({
        name: formatMessage(messages.apiConsole),
        href: urls.console,
        'data-test-id': `deployment-console-link`,
      }),
      ...(isOneConsoleEnabled
        ? [
            createItem({
              name: formatMessage(messages.oneConsole),
              href: urls.oneConsole,
              'data-test-id': `deployment-one-console-link`,
            }),
          ]
        : []),
    ]

    const editItem = createItem({
      name: formatMessage(messages.deploymentEdit),
      href: urls.edit,
      'data-test-id': `deployment-edit-link`,
      items: editSubItems,
    })

    const elasticsearchItems = createItem({
      name: formatMessage(messages.deploymentElasticsearch),
      href: urls.elasticsearch,
      'data-test-id': `deployment-index-elasticsearch-overview-link`,
      items: elasticsearchSubItems,
    })

    const sliderItems = getSupportedSliderInstanceTypesWithoutEs()
      .filter((sliderInstanceType) =>
        isSliderSupportedForDeployment({
          deployment: stackDeployment || undefined,
          deploymentTemplate: deploymentTemplate?.deployment_template,
          sliderInstanceType,
        }),
      )
      .filter(
        (sliderInstanceType) =>
          isAdminConsole ||
          (stackDeployment &&
            hasSizedSliderResource({
              deployment: stackDeployment,
              resourceType: sliderInstanceType,
            })),
      )
      .map((sliderInstanceType) =>
        createItem({
          name: formatMessage(getSliderPrettyName({ sliderInstanceType, version })),
          href: urls[sliderInstanceType],
          'data-test-id': `deployment-index-${sliderInstanceType}-overview-link`,
        }),
      )

    const activityItem = createItem({
      name: formatMessage(messages.deploymentActivity),
      href: urls.deploymentActivity,
      'data-test-id': `deployment-index-activity-link`,
      isSelected: pathname.startsWith(urls.deploymentActivity),
    })

    const securityItem = createItem({
      name: formatMessage(messages.deploymentSecurity),
      href: urls.security,
      'data-test-id': `deployment-index-elasticsearch-security-link`,
    })

    const operationsItem = createItem({
      name: formatMessage(messages.deploymentOperations),
      href: urls.operations,
      'data-test-id': `deployment-index-elasticsearch-operations-link`,
      items: [
        createItem({
          name: formatMessage(messages.deploymentHeapDumps),
          href: heapDumpsUrl(stackDeploymentId),
          'data-test-id': `deployment-index-elasticsearch-heap-dumps-link`,
        }),
      ],
    })

    const deploymentItems = [
      ...(editItem ? [editItem] : []),
      monitoringSectionNavItems,
      elasticsearchItems,
      ...sliderItems,
      activityItem,
      securityItem,
      operationsItem,
    ]

    return [
      createItem({
        name: getDeploymentName(),
        href: urls.root,

        /* WARNING: This data-test-id is being used to exclude tracked items from FullStory.
          This is a matter of PRIVACY, any changes here need approval of @amoldovan or @osman */
        'data-test-id': `deployment-index-overview-link`,
        items: deploymentItems,
      }),
    ]

    function getDeploymentName() {
      if (stackDeployment) {
        return getDisplayName({ deployment: stackDeployment })
      }

      if (stackDeploymentId) {
        return stackDeploymentId.slice(0, 6)
      }

      if (deployment) {
        return deployment.displayName
      }

      return stackDeploymentId.slice(0, 6)
    }
  }

  function platformNavigation(regionId) {
    const { defaultRegionId, trafficFilteringFeature } = props

    const { repositoryId } = params

    const platformLink = platformUrl(regionId)
    const adminconsolesLink = adminconsolesUrl(regionId)
    const containerSetsLink = containerSetsUrl(regionId)
    const hostsLink = hostsUrl(regionId)
    const elasticStackVersionsLink = elasticStackVersionsUrl(regionId)
    const trustManagementLink = platformTrustManagementUrl(regionId)
    const snapshotRepositoriesLink = snapshotRepositoriesUrl(regionId)
    const regionSettingsLink = regionSettingsUrl(regionId)
    const regionSecurityLink = regionSecurityUrl(regionId)
    const createSnapshotRepositoryLink = createSnapshotRepositoryUrl(regionId)
    const editSnapshotRepositoryLink = editSnapshotRepositoryUrl(regionId, repositoryId!)

    const containerSetItems = containerSetNavigation(regionId)
    const hostItems = hostNavigation(regionId)
    const topologyItems = topologyNavigation()
    const repositoryItems: EuiSideNavItem[] = []

    const defaultSnapshotRepository = isFeatureActivated(Feature.defaultSnapshotRepository)

    if (!defaultSnapshotRepository) {
      if (isCreateRepositoryActive(pathname)) {
        repositoryItems.push(
          createItem({
            name: formatMessage(messages.createRepository),
            href: createSnapshotRepositoryLink,
            'data-test-id': `platform-create-repository-link`,
          }),
        )
      }

      if (isEditSnapshotRepositoryActive(pathname)) {
        repositoryItems.push(
          createItem({
            name: repositoryId!,
            href: editSnapshotRepositoryLink,
            'data-test-id': `platform-edit-repository-link-${repositoryId}`,
          }),
        )
      }
    }

    const platformItems = [
      createItem({
        name: formatMessage(messages.platformHosts),
        href: hostsLink,
        items: hostItems,
        'data-test-id': `platform-hosts-link`,
      }),

      ...(isFeatureActivated(Feature.adminConsoles)
        ? [
            createItem({
              name: formatMessage(messages.platformAdminconsoles),
              href: adminconsolesLink,
              'data-test-id': `platform-admin-consoles-link`,
            }),
          ]
        : []),

      ...(isFeatureActivated(Feature.containerSets)
        ? [
            createItem({
              name: formatMessage(messages.platformContainerSets),
              href: containerSetsLink,
              items: containerSetItems,
              'data-test-id': `platform-container-sets-link`,
            }),
          ]
        : []),

      createItem({
        name: formatMessage(messages.platformElasticStack),
        href: elasticStackVersionsLink,
        'data-test-id': `platform-stack-link`,
      }),

      ...(isFeatureActivated(Feature.crossEnvCCSCCR)
        ? [
            createItem({
              name: formatMessage(messages.platformTrustManagement),
              href: trustManagementLink,
              'data-test-id': `platform-trust-link`,
            }),
          ]
        : []),

      createItem({
        name: formatMessage(messages.platformTopology),
        href: topologyUrl(regionId),
        isSelected: topologyItems != null || Boolean(matchPath(topologyUrl(`:regionId`), pathname)),
        items: topologyItems,
        'data-test-id': `platform-templates-link`,
      }),

      ...(defaultSnapshotRepository
        ? []
        : [
            createItem({
              name: formatMessage(messages.platformRepositories),
              href: snapshotRepositoriesLink,
              'data-test-id': `platform-repositories-link`,
              items: repositoryItems.length > 0 ? repositoryItems : undefined,
            }),
          ]),

      ...(trafficFilteringFeature
        ? [
            createItem({
              name: formatMessage(messages.platformSecurity),
              href: regionSecurityLink,
              'data-test-id': `platform-security-link`,
            }),
          ]
        : []),

      createItem({
        name: formatMessage(messages.platformSettings),
        href: regionSettingsLink,
        'data-test-id': `platform-settings-link`,
      }),
    ]

    // Only introduce an extra navigation level if there are multiple regions,
    // i.e. no default.
    if (defaultRegionId == null) {
      return [
        createItem({
          name: regionId,
          href: platformLink,
          items: platformItems,
        }),
      ]
    }

    platformItems.unshift(
      createItem({
        name: formatMessage(messages.platformSummary),
        href: platformLink,
      }),
    )

    return platformItems
  }

  function manageUsersNavigation(regionId) {
    const { defaultRegionId } = props

    const href = authenticationProvidersUrl(regionId)
    const managementItems = [
      createItem({
        name: formatMessage(messages.managementAuthenticationProviders),
        href,
      }),
      createItem({
        name: formatMessage(messages.managementUsers),
        href: manageNativeUsersUrl(regionId),
        'data-test-id': `manage-users-link`,
      }),
    ]

    managementItems.push(
      createItem({
        name: formatMessage(messages.managementApiKeys),
        href: manageAllUsersApiKeysUrl(regionId),
        'data-test-id': `manage-api-keys-link`,
      }),
    )

    // No extra level of menu items where there is a default region
    if (defaultRegionId != null) {
      return managementItems
    }

    return [
      createItem({
        name: regionId,
        href: manageRegionUrl(regionId),
        items: managementItems,
      }),
    ]
  }

  function userNavigation({ userId, profile }: { userId: string; profile?: SaasUserResponse }) {
    const userActive = isUserActive(pathname)
    const userCreateActive = isUserCreateActive(pathname)

    if (userActive) {
      return [
        createItem({
          name: profile != null ? profile.user.email : userId,
          href: userOverviewUrl(userId),
        }),
      ]
    }

    if (userCreateActive) {
      return [
        createItem({
          name: formatMessage(messages.createSaasUser),
          href: userCreateUrl(),
        }),
      ]
    }

    return
  }

  function organizationNavigation({
    organizationId,
    organization,
  }: {
    organizationId: string
    organization?: Organization
  }) {
    const organizationActive = isOrganizationActive(pathname)

    if (organizationActive) {
      return [
        createItem({
          name: organization != null ? organization.name : organizationId,
          href: organizationOverviewUrl(organizationId),
        }),
      ]
    }

    return
  }

  function containerSetNavigation(regionId) {
    const { containerSetId } = params

    const isContainerSetUrlActive =
      containerSetId != null && matchPath(containerSetUrl(`:regionId`, `:containerSetId`), pathname)

    if (!containerSetId) {
      return
    }

    if (!isContainerSetUrlActive) {
      return
    }

    function getContainerSetNavigationItems(regionId, containerSetId) {
      const { containerId } = params

      const isContainerUrlActive =
        containerId != null &&
        matchPath(containerUrl(`:regionId`, `:containerSetId`, `:containerId`), pathname)

      if (!containerId) {
        return
      }

      if (!isContainerUrlActive) {
        return
      }

      return [
        createItem({
          name: containerId,
          href: containerUrl(regionId, containerSetId, containerId),
        }),
      ]
    }

    const containerSetNavigationItems = getContainerSetNavigationItems(regionId, containerSetId)

    return [
      createItem({
        name: containerSetId,
        href: containerSetUrl(regionId, containerSetId),
        items: containerSetNavigationItems,
      }),
    ]
  }

  function hostNavigation(regionId) {
    const { hostId } = params

    const isHostUrlActive =
      hostId != null && matchPath(startingWith(hostUrl(`:regionId`, `:hostId`)), pathname)

    if (!hostId) {
      return
    }

    if (!isHostUrlActive) {
      return
    }

    return [
      createItem({
        name: hostId,
        href: hostUrl(regionId, hostId),
        'data-test-id': `platform-host-link`,
      }),
    ]
  }

  function topologyNavigation() {
    const { deploymentTemplate } = props

    const url = topologyViewDeploymentTemplateUrl(`:regionId`, `:templateId`)

    if (!matchPath(url, pathname)) {
      return
    }

    const { templateId } = params

    return [
      createItem({
        name: deploymentTemplate ? deploymentTemplate.name : templateId!,
        href: url,
        isSelected: true,
      }),
    ]
  }
}

export default ChromeNavigation

function renderNavLink({
  href = `/`,
  children,
  className,
  exact = false,
  beta = false,
  'data-test-id': dataTestId,
}: RenderNavLinkProps): ReactElement {
  const classes = ({ isActive }) =>
    cx(className, {
      'euiSideNavItemButton-beta': beta,
      'euiSideNavItemButton-isSelected': isActive,
    })

  const link = (
    <NavLink to={href} end={exact} className={classes} data-test-id={dataTestId}>
      {children}
    </NavLink>
  )

  if (!beta) {
    return link
  }

  return (
    <EuiFlexGroup gutterSize='s'>
      <EuiFlexItem grow={false}>{link}</EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiBetaBadge
          label={<FormattedMessage {...messages.beta} />}
          tooltipContent={<FormattedMessage {...messages.betaDescription} />}
        />
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

function setSelectedItem(
  items: EuiSideNavItem[],
  isSelected: (item: EuiSideNavItem) => boolean,
): EuiSideNavItem[] {
  return items.map(handleItemSelection)

  function handleItemSelection(item: EuiSideNavItem): EuiSideNavItem {
    if (isArray(item.items)) {
      return {
        ...item,
        items: setSelectedItem(item.items, isSelected),
        isSelected: isSelected(item),
      }
    } else if (isSelected(item)) {
      return {
        ...item,
        isSelected: true,
      }
    }

    return item
  }
}

function isDeploymentActive(pathname: string) {
  return matchPath(startingWith(deploymentUrl(`:deploymentId`)), pathname)
}

function isCreateDeploymentActive(pathname: string) {
  return matchPath(createDeploymentUrl(), pathname)
}

function isCreateRepositoryActive(pathname: string) {
  return matchPath(createSnapshotRepositoryUrl(`:regionId`), pathname)
}

function isEditSnapshotRepositoryActive(pathname: string) {
  return matchPath(editSnapshotRepositoryUrl(`:regionId`, `:deploymentId`), pathname)
}

function isPlatformActive(pathname: string) {
  if (matchPath(startingWith(regionsUrl()), pathname)) {
    return true
  }

  return (
    !isDeploymentActive(pathname) &&
    !isManagementActive(pathname) &&
    matchPath(startingWith(regionUrl(`:regionId`)), pathname)
  )
}

function isUserActive(pathname: string) {
  return matchPath(userOverviewUrl(`:userId`), pathname)
}

function isUserCreateActive(pathname: string) {
  return matchPath(userCreateUrl(), pathname)
}

function isOrganizationActive(pathname: string) {
  return matchPath(startingWith(organizationOverviewUrl(`:organizationId`)), pathname)
}

function isManagementActive(pathname: string) {
  return matchPath(startingWith(manageRegionUrl(`:regionId`)), pathname)
}

function isAdvancedDeploymentEditActive(pathname: string) {
  return matchPath(deploymentAdvancedEditUrl(`:deploymentId`), pathname)
}
